import type { NumberInputBounds } from '#layers/data-model/field-types/number';
import type { InferPrimitiveTypeName, PrimitiveTypeInteger64 } from '#layers/data-model/types/schema/primitive-type';
import type { Int64, UInt64 } from '#layers/data-model/types/schema/values';
import { INTEGER_64_BOUNDS } from '#layers/data-model/constants/schema/primitive-type';

interface Integer64Values {
	int64: Int64;
	uint64: UInt64;
}

/**
 * The branded value of a 64-bit integer type. Kept in step with the primitive types' value types
 * by the field parser registration, which only compiles while the two agree.
 */
export type Integer64Value<N extends InferPrimitiveTypeName<PrimitiveTypeInteger64>> = Integer64Values[N];

const INTEGER_TEXT = /^-?\d+$/;

/** The only place the brands are created: `value` is range-checked for `name` right here. */
function fromBigInt<N extends InferPrimitiveTypeName<PrimitiveTypeInteger64>>(value: bigint, name: N): Integer64Value<N> | undefined {
	const { min, max } = INTEGER_64_BOUNDS[name];
	if (value < BigInt(min) || value > BigInt(max)) return undefined;

	return value.toString() as Integer64Value<N>;
}

/** Parses integer text, or returns `undefined` if it isn't a plain integer within the type's range. */
export function parseInteger64Text<N extends InferPrimitiveTypeName<PrimitiveTypeInteger64>>(text: string, name: N): Integer64Value<N> | undefined {
	const trimmed = text.trim();
	return INTEGER_TEXT.test(trimmed) ? fromBigInt(BigInt(trimmed), name) : undefined;
}

/**
 * Parses an untyped wire or stored value: a decimal string, or a safe-integer number (e.g. a static
 * default stored as a JSON number). Larger numbers were already rounded when JSON was parsed, so
 * they can't prove an exact value.
 */
export function parseInteger64Value<N extends InferPrimitiveTypeName<PrimitiveTypeInteger64>>(value: unknown, name: N): Integer64Value<N> | undefined {
	if (typeof value === 'string') return parseInteger64Text(value, name);
	if (typeof value === 'bigint') return fromBigInt(value, name);
	if (typeof value === 'number' && Number.isSafeInteger(value)) return fromBigInt(BigInt(value), name);
	return undefined;
}

/** Creates a value known to be in range, such as a literal; throws a `RangeError` otherwise. */
export function toInteger64<N extends InferPrimitiveTypeName<PrimitiveTypeInteger64>>(value: bigint, name: N): Integer64Value<N> {
	const result = fromBigInt(value, name);
	if (result === undefined) throw new RangeError(`${value} is out of range for ${name}`);
	return result;
}

/** Reads number-input bounds from untyped stored input options, dropping anything else. */
export function parseNumberInputBounds(options: Record<string, unknown> = {}): NumberInputBounds {
	return {
		min: toBound(options.min),
		max: toBound(options.max),
		step: toBound(options.step),
	};
}

function toBound(value: unknown): number | string | undefined {
	return typeof value === 'number' || typeof value === 'string' ? value : undefined;
}

/**
 * Props for the string-backed decimal input editing a 64-bit integer: the configured number bounds,
 * narrowed to the type's range, with no fractional digits.
 */
export function getInteger64InputProps(name: InferPrimitiveTypeName<PrimitiveTypeInteger64>, configured: NumberInputBounds = {}) {
	const { lower, upper } = getRange(name, configured);
	const step = toInteger(configured.step);

	return {
		min: lower.toString(),
		max: upper.toString(),
		step: step === undefined ? undefined : String(step > 1n ? step : 1n),
		scale: 0,
	};
}

/**
 * Snaps a value to the configured step, as the number input's step snapping does: relative to the
 * configured `min` (or 0), halves rounded away from it, and kept within the narrowed range.
 */
export function snapInteger64ToStep<N extends InferPrimitiveTypeName<PrimitiveTypeInteger64>>(value: Integer64Value<N>, name: N, configured: NumberInputBounds = {}): Integer64Value<N> {
	const size = toInteger(configured.step);
	if (size === undefined || size <= 1n) return value;

	const { lower, upper } = getRange(name, configured);
	const base = toInteger(configured.min) ?? 0n;
	const target = BigInt(value);

	const remainder = (target - base) % size;
	const distance = remainder < 0n ? -remainder : remainder;
	let snapped = distance * 2n >= size ? target + (remainder < 0n ? -1n : 1n) * (size - distance) : target - remainder;

	if (snapped < lower) snapped += size;
	if (snapped > upper) snapped -= size;

	return fromBigInt(snapped, name) ?? value;
}

function getRange(name: InferPrimitiveTypeName<PrimitiveTypeInteger64>, configured: NumberInputBounds) {
	const lower = BigInt(INTEGER_64_BOUNDS[name].min);
	const upper = BigInt(INTEGER_64_BOUNDS[name].max);
	const min = toInteger(configured.min);
	const max = toInteger(configured.max);

	return {
		lower: min !== undefined && min > lower ? min : lower,
		upper: max !== undefined && max < upper ? max : upper,
	};
}

/** A configured bound as an integer: integer text exactly, other numbers truncated. */
function toInteger(value: number | string | undefined): bigint | undefined {
	if (typeof value === 'string' && INTEGER_TEXT.test(value.trim())) return BigInt(value.trim());

	const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
	return typeof number === 'number' && Number.isFinite(number) ? BigInt(Math.trunc(number)) : undefined;
}
